import dayjs from "dayjs";
import { Button, Tabs, Menu, Dropdown } from "antd";
import { ReactComponent as FolderOpenIcon } from "Icons/vuesax/folder-open.svg";
import { ReactComponent as ImportIcon } from "Icons/vuesax/import.svg";
import { ReactComponent as ShieldSearchIcon } from "Icons/vuesax/shield-search.svg";
import { ReactComponent as HamburgerIcon } from "Icons/vuesax/menu.svg";
import { ReactComponent as AddIcon } from "../../Icons/vuesax/add.svg";
import { ReactComponent as ViewsIcon } from "../../Icons/vuesax/document-cloud.svg";
import { ReactComponent as DocumentCloudIcon } from "../../Icons/vuesax/document-cloud.svg";
import { useState, useMemo, useEffect, useRef } from "react";
import ArchiveFilters from "./ArchiveFilters";
import JobGrid from "./JobGrid";
import { DefaultDateType, JobStatus } from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { WebDPrivilege } from "api/services/models/WebDUser";
import { getPageKey } from "helpers/utils";
import { useLayoutContext } from "components/MainContainer";
import RecoveryEmailForm from "components/RecoveryEmailForm";
import { calculateDate } from "components/DefaultArchiveFilters";

const { TabPane } = Tabs;
type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

interface JobsProps {
  page?: string;
}

const Jobs: React.FC<JobsProps> = ({ page }) => {
  const [activeKey, setActiveKey] = useState<string>(page ?? "1");
  const [views, setViews] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const { accountId, webDUser } = useTDAuthentication();
  const userId = webDUser?.user.userId;
  const [tabs, setTabs] = useState<any>([]);
  const { toggleDrawer, isSmallScreen } = useLayoutContext();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const archiveFiltersRef = useRef<any>(null);

  const remove = (targetKey: string) => {
    if (!tabs) return;
    const targetIndex = tabs.findIndex((item: any) => item.key === targetKey);
    const newItems = tabs.filter((item: any) => item.key !== targetKey);
    if (newItems.length && targetKey === activeKey) {
      const newActiveKey =
        newItems[targetIndex === newItems.length ? targetIndex - 1 : targetIndex].key;
      setActiveKey(newActiveKey);
    }
    setTabs(newItems);
  };

  const onEdit = (targetKey: TargetKey, action: "add" | "remove") => {
    if (action === "remove") {
      if (targetKey === "1" || targetKey === "2" || targetKey === "3" || targetKey==="views") {
        return;
      }

      const newTabs = tabs.filter((tab: any) => tab.key !== targetKey);
      setTabs(newTabs);

      if (activeKey === targetKey && newTabs.length > 0) {
        setActiveKey(newTabs[0].key);
      }
    }
  };

  const handleChange = (key: string) => {
    setActiveKey(key);
    if (key === "views") {
      addTab();
    }
    if (key === "1" || key === "2" ||key=="3") {
      setRefreshKey(prev => prev + 1);
    }
  };

  useEffect(()=> {if (activeKey === "views") {
    addTab();
  }}, [accountId])

  useEffect(() => {
    const recoveryEmailPopupShown = sessionStorage.getItem("recoveryEmailPrompted");
    if (!recoveryEmailPopupShown && webDUser?.user.email == null) {
      sessionStorage.setItem("recoveryEmailPrompted", "true");
      showModal();
    }
  }, []);

  useEffect(() => {
    if (isModalVisible) {
      document.body.classList.add("blur");
    } else {
      document.body.classList.remove("blur");
    }

    return () => {
      document.body.classList.remove("blur");
    };
  }, [isModalVisible]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", !isSmallScreen);
  }, [isSmallScreen]);

  const handleShowModal = () => {
    archiveFiltersRef.current?.showModal();
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const closeRecoveryEmailModal = () => {
      setIsModalVisible(false);
  };

  const addTab = () => {
    const existingViews = JSON.parse(localStorage.getItem("archiveFilterViews")!) || [];
    const filteredViews = existingViews.filter(
      (view: any) => (view.userId === userId && view.accountId === accountId) || view.defaultView == true
    );
    const viewNames = filteredViews.map((view: any) => view.viewName);
    setViews(viewNames);
  };

  const prepareFiltersData = (data: any) => {
    let filteredData = {
      selectedServiceDateFrom: dayjs(data.selectedServiceDateFrom).isValid()
        ? dayjs(data.selectedServiceDateFrom)
        : null,
      selectedServiceDateTo: dayjs(data.selectedServiceDateTo).isValid()
        ? dayjs(data.selectedServiceDateTo)
        : null,
      selectedDictationDateFrom: dayjs(data.selectedDictationDateFrom).isValid()
        ? dayjs(data.selectedDictationDateFrom)
        : null,
      selectedDictationDateTo: dayjs(data.selectedDictationDateTo).isValid()
        ? dayjs(data.selectedDictationDateTo)
        : null,
      selectedFirstName: data.selectedFirstName,
      selectedLastName: data.selectedLastName,
      selectedMRN: data.selectedMRN,
      selectedDOB: dayjs(data.selectedDOB).isValid() ? dayjs(data.selectedDOB) : null,
      selectedProviders: data.selectedProviders || [],
      selectedReportTypes: data.selectedReportTypes || [],
      selectedJobId: data.selectedJobId || "",
      selectedRegions: data.selectedRegions || [],
      selectedLocations: data.selectedLocations || [],
      selectedJobStatuses: data.selectedJobStatuses || [],
      defaultDateType: data.defaultDateType || DefaultDateType.None,
      renderData: dayjs()
    };

    if (filteredData.defaultDateType !== DefaultDateType.None) {
      const result = calculateDate(filteredData.defaultDateType);
      switch (filteredData.defaultDateType) {
        case DefaultDateType.DOSCurrentWeek:
        case DefaultDateType.DOSLastWeek:
        case DefaultDateType.DOSCurrentMonth:
        case DefaultDateType.DOSLastMonth:
          filteredData.selectedServiceDateFrom = result!.from;
          filteredData.selectedServiceDateTo = result!.to;
          break;
        case DefaultDateType.DODCurrentWeek:
        case DefaultDateType.DODLastWeek:
        case DefaultDateType.DODCurrentMonth:
        case DefaultDateType.DODLastMonth:
          filteredData.selectedDictationDateFrom = result!.from;
          filteredData.selectedDictationDateTo = result!.to;
          break;
      }
    }
    return filteredData;
  };

  const [showResults, setShowResults] = useState("");

  const handleShowResults = (value: any) => {
    setShowResults(value);
  };

  const handleSelectView = (viewName: string) => {
    const existingViews = JSON.parse(localStorage.getItem("archiveFilterViews")!) || [];
    const selectedView = existingViews.find((view: any) => view.viewName === viewName);
    if (selectedView) {
      const preparedData = prepareFiltersData(selectedView);
      const existingTabIndex = tabs.findIndex(
      (tab: any) => tab.label === "Archive"
      );
      const updatedTabs = [...tabs];
      const updatedTab = {
        ...updatedTabs[existingTabIndex],
        content: (
          <ArchiveFilters
            ref={archiveFiltersRef}
            {...preparedData}
            onShowResultsChange={handleShowResults}
          />
        ),
      };

      updatedTabs[existingTabIndex] = updatedTab;
      setTabs(updatedTabs);
      setActiveKey(updatedTab.key);
    }
  };

  const createPreparedFilters = (jobStatus: JobStatus) => ({
    jobStatus: [jobStatus],
    strPatientName: undefined,
    strMRN: undefined,
    strProviderId: undefined,
    strReportType: undefined,
    dtDOSFrom: undefined,
    dtDOSTo: undefined,
    dtDODFrom: undefined,
    dtDODTo: undefined,
    jobApprovalStatus: undefined,
    strRegion: undefined,
    strLocation: undefined,
    dtPatDOB: undefined,
    VTJobId: undefined,
    qcholdFilter: undefined
  });

  const preparedInboxFilters = useMemo(() => createPreparedFilters(JobStatus.Ready), []);
  const preparedQCHoldFilters = useMemo(() => createPreparedFilters(JobStatus.QCHold), []);

  const memoizedDefaultTabs = useMemo(
    () => [
      {
        key: "1",
        label: "Inbox",
        icon: (
          <ImportIcon className="tab-icon" style={{ width: ".875rem", height: ".875rem", strokeWidth: activeKey === "1" ? 3 : 2 }} />
        ),
        content:
          activeKey === "1" ? (
            <JobGrid preparedFilters={preparedInboxFilters} source={"Inbox"} key={refreshKey} />
          ) : null,
        disabled: !webDUser?.hasPrivilege(WebDPrivilege.ViewInbox)
      },
      {
        key: "2",
        label: "QC Hold",
        icon: (
          <ShieldSearchIcon
            className="tab-icon"
            style={{ width: ".875rem", height: ".875rem", strokeWidth: activeKey === "2" ? 3 : 2 }}
          />
        ),
        content:
          activeKey === "2" ? (
            <JobGrid
              preparedFilters={preparedQCHoldFilters}
              source={"QCHold"}
              key={refreshKey}
            />
          ) : null,
        disabled: !webDUser?.hasPrivilege(WebDPrivilege.ViewQCHold)
      },
      {
        key: "3",
        label: "Archive",
        icon: (
          <FolderOpenIcon
            className="tab-icon"
            style={{ width: ".875rem", height: ".875rem", strokeWidth: activeKey === "3" ? 1.5 : 1 }}
          />
        ),
        content:  activeKey === "3" ? <ArchiveFilters ref={archiveFiltersRef} onShowResultsChange={handleShowResults}
        key={refreshKey}/> : null,
        disabled: !webDUser?.hasPrivilege(WebDPrivilege.ViewArchive)
      }
    ],
    [refreshKey, webDUser, accountId]
  );

  useEffect(() => {
    const updatedTabs = tabs.map((tab: any) => {
      const defaultTab = memoizedDefaultTabs.find(defaultTab => defaultTab.key === tab.key);
      return defaultTab ? defaultTab : tab;
    });

    const missingDefaultTabs = memoizedDefaultTabs.filter(
      defaultTab => !tabs.some((tab: any) => tab.key === defaultTab.key)
    );
    setTabs([...updatedTabs, ...missingDefaultTabs]);
    setActiveKey(
      activeKey !== "1" && activeKey !== "2" && activeKey !== "3" && activeKey !=="views"
        ? getPageKey(webDUser?.user.landingPage)
        : activeKey
    );
  }, [memoizedDefaultTabs]);

  const menu = (
    <Menu>
      {views.map(viewName => (
        <Menu.Item key={viewName} onClick={() => handleSelectView(viewName)}>
          {viewName}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <>
      {!isSmallScreen ? (
        <div className="container">
          <div className="header">
            <div className="header-title">
              <span>Jobs</span>
            </div>
          </div>

          <div>
            <Tabs
              activeKey={activeKey}
              onChange={handleChange}
              tabBarExtraContent={
                <>
                  <div style={{ display: activeKey === "3" && !showResults ? "flex" : "none" }}>
                    <Button onClick={handleShowModal} className="save-view-button">
                      <DocumentCloudIcon width="16px" height="16px" />
                      Save Filter
                    </Button>
                    <Dropdown overlay={menu} trigger={["click"]}>
                      <Button
                        type="text"
                        onClick={addTab}
                        disabled={!webDUser?.hasPrivilege(WebDPrivilege.ViewArchive)}>
                        <div>Saved Filters</div>
                      </Button>
                    </Dropdown>
                  </div>
                </>
              }
              onEdit={onEdit}
              hideAdd>
              {tabs.map((tab: any) => (
                <TabPane
                  tab={
                    <span className="tab-label">
                      {tab.icon}
                      {tab.label}
                      {!(tab.key === "1" || tab.key === "2" || tab.key === "3") && (
                        <div
                          className="close-icon"
                          onClick={e => {
                            e.stopPropagation();
                            remove(tab.key);
                          }}>
                          <AddIcon />
                        </div>
                      )}
                    </span>
                  }
                  disabled={tab.disabled}
                  key={tab.key}
                  closable={!(tab.key === "1" || tab.key === "2" || tab.key === "3")}>
                  {tab.content}
                </TabPane>
              ))}
            </Tabs>
          </div>
        </div>
      ) : (
        <div className="container">
          <div className="header-mobile">
            <div className="header-title">
              <Button
                type="text"
                icon={<HamburgerIcon />}
                onClick={toggleDrawer}
                className="hamburger-menu-button"
                style={{ marginRight: ".25rem" }}
              />
              <span>Jobs</span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <div
              style={{
                flexGrow: 1,
                overflowY: "auto",
                backgroundColor: "#FAFAFA",
                marginBottom: "3.2rem"
              }}>
              {tabs.find((tab: any) => tab.key === activeKey)?.content}
              {activeKey == "views" && (
                <>
                  <span className="font-1212" style={{ fontSize: "1rem" }}>
                    Please choose a view:
                  </span>
                  {views.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop:"1.25rem" }}>
                      {views.map((view, index) => (
                        <div
                          key={index}
                          onClick={() => handleSelectView(view)}
                          className="view-card">
                          <div
                            style={{
                              marginRight: "0.8rem",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center"
                            }}>
                            <ViewsIcon
                              className="tab-icon"
                              style={{ width: ".875rem", height: ".875rem", color:"#1868db" }}
                            />
                          </div>
                          <span>{view}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="font-1212" style={{fontSize:".875rem", marginTop:"1.25rem" }}>  
                      No Views Found
                    </span>
                  )}
                </>
              )}
            </div>
            <div
              style={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: "#fff",
                borderTop: "1px solid #f0f0f0",
                overflowX: "auto",
                whiteSpace: "nowrap"
              }}>
              <Tabs
                activeKey={activeKey}
                onChange={handleChange}
                hideAdd
                centered
                className="tabs-mobile"
                tabBarStyle={{ margin: 0 }}
                onEdit={onEdit}>
                {tabs.map((tab: any) => (
                  <TabPane
                    tab={
                      <span
                        className={`tab-label ${activeKey === tab.key ? "active-mobile" : ""}`}>
                        {tab.icon}
                        {activeKey === tab.key && <span className="tab-text">{tab.label}</span>}
                        {!(tab.key === "1" || tab.key === "2" || tab.key === "3" || tab.key==="views") && (
                          <div
                            className="close-icon"
                            onClick={e => {
                              e.stopPropagation();
                              remove(tab.key);
                            }}>
                            <AddIcon />
                          </div>
                        )}
                      </span>
                    }
                    key={tab.key}
                    disabled={tab.disabled}
                    closable={
                      !(
                        tab.key === "1" ||
                        tab.key === "2" ||
                        tab.key === "3" ||
                        tab.key === "views"
                      )
                    }
                  />
                ))}
                <TabPane
                  tab={
                    <span
                      className={`tab-label ${activeKey === "views" ? "active-mobile" : ""}`}>
                      <ViewsIcon
                        className="tab-icon"
                        style={{ width: ".875rem", height: ".875rem", strokeWidth: activeKey === "views" ? 2.5 : 1.5 }}
                      />
                      {activeKey === "views" && <span className="tab-text">Views</span>}
                    </span>
                  }
                  key="views"
                  closable={true}
                  disabled={!webDUser?.hasPrivilege(WebDPrivilege.ViewArchive)}
                />
              </Tabs>
            </div>
          </div>
        </div>
      )}
      {isModalVisible && <RecoveryEmailForm onClose={closeRecoveryEmailModal}/>}
    </>
  );
};

export default Jobs;
